import { Badge } from "@design";
import { useTranslation } from "react-i18next";
import type { BrandTemplateDetails } from "@/api";
import { InlineNotice } from "@/components/InlineNotice";
import { Text } from "@/components/Text";
import type { TemplateFormat } from "./useBrandTemplates";

const VISIBLE_STYLES = 12;
const THEME_COLOUR_ORDER = [
  "dk1",
  "lt1",
  "dk2",
  "lt2",
  "accent1",
  "accent2",
  "accent3",
  "accent4",
  "accent5",
  "accent6",
];

/** Theme colours in the theme's own order, then anything else the template defined. */
function themeColours(colours: Record<string, string>): Array<[string, string]> {
  const known = THEME_COLOUR_ORDER.filter((key) => colours[key]).map((key): [string, string] => [key, colours[key]]);
  const rest = Object.entries(colours).filter(([key]) => !THEME_COLOUR_ORDER.includes(key));
  return [...known, ...rest];
}

/** What was read from an uploaded template: warnings, layouts or styles, theme colours and fonts. */
export function TemplateDetails({
  format,
  details,
}: Readonly<{ format: TemplateFormat; details: BrandTemplateDetails }>) {
  const { t } = useTranslation("brands", { keyPrefix: "templates" });
  const layouts = details.layouts ?? [];
  const styles = details.styles ?? [];
  const colours = themeColours(details.themeColours ?? {});
  const fonts = details.themeFonts;
  const slideCount = details.slideCount ?? 0;
  const sheets = details.sheets ?? [];
  const cellStyles = details.cellStyles ?? [];
  const tableCount = details.tableCount ?? 0;

  return (
    <div className="flex flex-col gap-3">
      {format === "pptx" && slideCount > 0 && <InlineNotice tone="warning">{t("warnings.sampleSlides")}</InlineNotice>}
      {format === "pptx" && (layouts.length === 0 || details.masterCount === 0) && (
        <InlineNotice tone="warning">{t("warnings.noLayouts")}</InlineNotice>
      )}
      {format === "docx" && (details.paragraphCount ?? 0) > 0 && (
        <InlineNotice tone="warning">{t("warnings.sampleParagraphs")}</InlineNotice>
      )}
      {format === "xlsx" && (details.valueCellCount ?? 0) > 0 && (
        <InlineNotice tone="warning">{t("warnings.sampleData")}</InlineNotice>
      )}

      {format === "pptx" && layouts.length > 0 && (
        <NameList
          label={t("layouts")}
          names={layouts}
          note={slideCount > 0 ? t("slideCount", { count: slideCount }) : undefined}
        />
      )}
      {format === "docx" && styles.length > 0 && (
        <NameList
          label={t("styles")}
          names={styles.slice(0, VISIBLE_STYLES)}
          note={styles.length > VISIBLE_STYLES ? t("moreStyles", { count: styles.length - VISIBLE_STYLES }) : undefined}
        />
      )}
      {format === "xlsx" && sheets.length > 0 && (
        <NameList
          label={t("sheets")}
          names={sheets}
          note={tableCount > 0 ? t("tableCount", { count: tableCount }) : undefined}
        />
      )}
      {format === "xlsx" && cellStyles.length > 0 && (
        <NameList
          label={t("cellStyles")}
          names={cellStyles.slice(0, VISIBLE_STYLES)}
          note={
            cellStyles.length > VISIBLE_STYLES
              ? t("moreStyles", { count: cellStyles.length - VISIBLE_STYLES })
              : undefined
          }
        />
      )}

      {colours.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <Text variant="eyebrow" tone="secondary">
            {t("themeColors")}
          </Text>
          <ul className="flex flex-wrap gap-1.5">
            {colours.map(([key, value]) => (
              <li key={key} title={`${key} ${value}`}>
                <span
                  aria-hidden="true"
                  className="block size-6 rounded border border-border-subtle"
                  // conventions-ignore: no-raw-colors swatches render the template's theme hex.
                  style={{ backgroundColor: value }}
                />
                <span className="sr-only">{`${key} ${value}`}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(fonts?.heading || fonts?.body) && (
        <div className="flex flex-col gap-0.5">
          <Text variant="eyebrow" tone="secondary">
            {t("themeFonts")}
          </Text>
          <Text variant="body-sm">
            {t("fonts", { heading: fonts.heading || t("fontUnknown"), body: fonts.body || t("fontUnknown") })}
          </Text>
        </div>
      )}
    </div>
  );
}

function NameList({ label, names, note }: Readonly<{ label: string; names: string[]; note?: string }>) {
  return (
    <div className="flex flex-col gap-1.5">
      <Text variant="eyebrow" tone="secondary">
        {label}
      </Text>
      <ul className="flex flex-wrap gap-1.5">
        {[...new Set(names)].map((name) => (
          <li key={name}>
            <Badge size="compact">{name}</Badge>
          </li>
        ))}
        {note && (
          <li className="self-center">
            <Text as="span" variant="caption" tone="tertiary">
              {note}
            </Text>
          </li>
        )}
      </ul>
    </div>
  );
}
